import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { GameEntry, GameSystemId } from '@shared/types.ts';
import { api } from '@/lib/api.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Modal } from '@/ui/Modal.tsx';
import { Strip } from '@/ui/Strip.tsx';

/**
 * The Games shelf — NES, Super Nintendo and arcade.
 *
 * THE GAME RUNS ON THIS DEVICE, not on the server. Pressing a tile leaves the
 * app for /games/player.html, which loads EmulatorJS, fetches the ROM and
 * plays it here — so there is no stream, no lag beyond the TV's own, and
 * nothing for N8-Server's GPU to do. The server keeps the shelf, the saves and
 * each person's stars.
 *
 * FINDING ONE OF SIX HUNDRED: a search box filtering by title, console, year
 * and maker; a Favorites row; Continue playing. The native TV app's shelf
 * (androidtv/tv GamesScreen.kt) works the same way.
 *
 * Click plays. Right-click, a long press, or MENU on a remote opens the
 * game's options — resume, start over, star.
 */
export function GamesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['games'],
    queryFn: () => api.games(),
    // The shelf is a folder on disk; somebody copying ROMs in will come back
    // and look, so do not sit on a stale answer for long.
    staleTime: 15_000,
  });
  const [system, setSystem] = useState<GameSystemId | 'all'>('all');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<GameEntry | null>(null);

  // Xbox 360 / PS3 stream through Moonlight, which only the TV app can open.
  const games = useMemo(() => (data?.games ?? []).filter((g) => !g.streamed), [data]);
  const systems = useMemo(() => (data?.systems ?? []).filter((s) => !s.streamed), [data]);
  const systemName = (id: GameSystemId) => systems.find((s) => s.id === id)?.name ?? id;

  /*
   * Every word typed must appear somewhere in the title, console, year or
   * maker, in any order, ignoring case and punctuation — "mario 3" finds
   * "Super Mario Bros. 3".
   */
  const words = useMemo(() => normalize(query).split(' ').filter(Boolean), [query]);
  const matching = useMemo(() => games.filter((g) => {
    if (!words.length) return true;
    const hay = normalize([g.title, systemName(g.system), g.year, g.maker].filter(Boolean).join(' '));
    return words.every((w) => hay.includes(w));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [games, words, systems]);

  const shown = useMemo(
    () => (system === 'all' ? matching : matching.filter((g) => g.system === system)),
    [matching, system],
  );
  const resumable = useMemo(() => games.filter((g) => g.hasResume), [games]);
  const favorites = useMemo(() => matching.filter((g) => g.favorite), [matching]);
  const searching = words.length > 0;

  /*
   * A STAR CHANGES THE SCREEN AT ONCE; the server is told behind it, and the
   * shelf re-read afterwards so a refusal shows the truth.
   */
  async function toggleFavorite(g: GameEntry) {
    const on = !g.favorite;
    qc.setQueryData(['games'], (old: typeof data) => old && ({
      ...old,
      games: old.games.map((x) => (x.id === g.id ? { ...x, favorite: on } : x)),
    }));
    setOptions((o) => (o && o.id === g.id ? { ...o, favorite: on } : o));
    try { await api.setGameFavorite(g.id, on); } finally {
      void qc.invalidateQueries({ queryKey: ['games'] });
    }
  }

  // MENU on a remote opens the options of whichever game holds the cursor.
  useEffect(() => pushKeyLayer((key: NavKey) => {
    if (key !== 'menu') return false;
    const id = (document.activeElement as HTMLElement | null)
      ?.getAttribute?.('data-nav')?.match(/^game-(?:resume-|fav-)?([a-f0-9]{16})$/)?.[1];
    const g = id && games.find((x) => x.id === id);
    if (!g) return false;
    setOptions(g);
    return true;
  }), [games]);

  /*
   * A suspended game ASKS, everything else just starts. Resuming is what
   * somebody nearly always wants, but "Start over" has to be one press away
   * for the brother who wants his own run of Mario.
   */
  const choose = (g: GameEntry) => {
    if (g.hasResume) setOptions(g);
    else launch(g, false);
  };

  if (isLoading) return <div className="muted center">Loading games…</div>;
  if (error) return <div className="muted center">Could not load the games shelf.</div>;

  if (!games.length) {
    return (
      <div className="games-empty">
        <h2 className="libe-title">No games on the shelf yet</h2>
        <p className="muted">
          Copy ROMs onto the server into folders named <code>nes</code>, <code>snes</code>,
          {' '}<code>arcade</code> (FinalBurn Neo sets), <code>mame2003</code> or <code>psx</code>
          {' '}(PlayStation discs, CHD preferred), inside the
          games folder. They appear here within a minute.
        </p>
      </div>
    );
  }

  const tile = (g: GameEntry, id: string, sub: string, onSelect: () => void, autoFocus = false) => (
    <GameTile key={id} game={g} id={id} sub={sub} onSelect={onSelect}
      onOptions={() => setOptions(g)} autoFocus={autoFocus} />
  );

  return (
    <>
      <div className="games-top">
        <input
          className="games-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${games.length} games`}
          aria-label="Search games"
          data-nav="games-search"
        />
        {/* Only offered when there is more than one console to choose between. */}
        {systems.length > 1 && (
          <div className="games-filter" role="tablist" aria-label="Systems">
            <Focusable
              id="games-sys-all"
              className={`games-chip${system === 'all' ? ' on' : ''}`}
              onSelect={() => setSystem('all')}
            >
              All <span className="games-count">{matching.length}</span>
            </Focusable>
            {systems.map((s) => (
              <Focusable
                key={s.id}
                id={`games-sys-${s.id}`}
                className={`games-chip${system === s.id ? ' on' : ''}`}
                onSelect={() => setSystem(s.id)}
              >
                {s.name} <span className="games-count">
                  {searching ? matching.filter((g) => g.system === s.id).length : s.count}
                </span>
              </Focusable>
            ))}
          </div>
        )}
      </div>

      {system === 'all' && !searching && resumable.length > 0 && (
        <section className="libe-row">
          <h2 className="libe-title">Continue playing</h2>
          <Strip>
            {resumable.map((g, i) =>
              tile(g, `game-resume-${g.id}`, systemName(g.system), () => launch(g, true), i === 0))}
          </Strip>
        </section>
      )}

      {system === 'all' && favorites.length > 0 && (
        <section className="libe-row">
          <h2 className="libe-title">Favorites</h2>
          <Strip>
            {favorites.map((g) => tile(g, `game-fav-${g.id}`, systemName(g.system), () => choose(g)))}
          </Strip>
        </section>
      )}

      {searching && shown.length === 0 && (
        <div className="muted center">No games match “{query}”.</div>
      )}

      <div className="libe-grid games-grid">
        {shown.map((g, i) => tile(
          g,
          `game-${g.id}`,
          [g.year, g.maker, system === 'all' ? systemName(g.system) : null].filter(Boolean).join(' · '),
          () => choose(g),
          i === 0 && !(system === 'all' && !searching && resumable.length > 0),
        ))}
      </div>

      {options && (
        <Modal className="account-dialog" onClose={() => setOptions(null)} labelledBy="game-ask-title">
          <div className="menu-head">
            <div>
              <div className="big" id="game-ask-title">{options.title}</div>
              {options.hasResume && <div className="muted">You have a game in progress.</div>}
            </div>
          </div>
          <div className="menu-items">
            {options.hasResume ? (
              <>
                <Focusable id="game-ask-resume" className="btn menu-item" autoFocus
                  onSelect={() => launch(options, true)}>
                  Resume where I left off
                </Focusable>
                <Focusable id="game-ask-new" className="btn menu-item"
                  onSelect={() => launch(options, false)}>
                  Start over
                </Focusable>
              </>
            ) : (
              <Focusable id="game-ask-play" className="btn menu-item" autoFocus
                onSelect={() => launch(options, false)}>
                Play
              </Focusable>
            )}
            <Focusable id="game-ask-fav" className="btn menu-item"
              onSelect={() => { void toggleFavorite(options); }}>
              {options.favorite ? 'Remove from favorites' : 'Add to favorites'}
            </Focusable>
            <Focusable id="game-ask-close" className="btn menu-item" onSelect={() => setOptions(null)}>
              Cancel
            </Focusable>
          </div>
        </Modal>
      )}
    </>
  );
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Leave the app for the player page.
 *
 * A real navigation, not a route change: see player.js for why the emulator
 * gets a page of its own. `assign` rather than `replace`, so Back from a
 * player that failed to load returns here.
 */
function launch(g: GameEntry, resume: boolean) {
  window.location.assign(`/games/player.html?id=${encodeURIComponent(g.id)}&resume=${resume ? 1 : 0}`);
}

/**
 * One game. The same tile as a film, with the art CONTAINED rather than
 * cropped: a Super Nintendo box is landscape, an NES box is portrait and an
 * arcade title screen is 4:3, and cropping all three to a film poster's 2:3
 * cuts the logo off two of them.
 *
 * Options: right-click for a mouse, a long press for a finger (the browser
 * claims its own, so it is timed here), MENU for a remote (see the key layer
 * in GamesPage).
 */
function GameTile({
  game, id, sub, onSelect, onOptions, autoFocus,
}: {
  game: GameEntry; id: string; sub: string; onSelect(): void; onOptions(): void; autoFocus?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const [holdTimer, setHoldTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [held, setHeld] = useState(false);
  const endHold = () => { if (holdTimer) clearTimeout(holdTimer); setHoldTimer(null); };
  return (
    <Focusable
      id={id}
      className="libe-card game-card"
      onSelect={() => { if (held) { setHeld(false); return; } onSelect(); }}
      autoFocus={autoFocus}
    >
      <div
        className="libe-art game-art"
        onContextMenu={(e) => { e.preventDefault(); onOptions(); }}
        onPointerDown={(e) => {
          if (e.pointerType !== 'touch') return;
          setHeld(false);
          setHoldTimer(setTimeout(() => { setHeld(true); onOptions(); }, 500));
        }}
        onPointerUp={endHold}
        onPointerCancel={endHold}
        onPointerLeave={endHold}
      >
        {game.art && !broken
          ? <img src={game.art} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
          : <span className="game-noart">{game.title}</span>}
        <div className="game-marks">
          {game.favorite && <span className="game-mark" aria-label="Favorite">★</span>}
          {game.hasResume && <span className="game-mark" aria-label="In progress">▶</span>}
        </div>
        <div className="res-overlay">
          {sub && <div className="res-ov-year">{sub}</div>}
          <div className="res-ov-title">{game.title}</div>
        </div>
      </div>
    </Focusable>
  );
}
